import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton
} from "@mui/material";
import { AppAction, AppContext } from "helpers/context";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  EXTREME_KEY_LAYER_RESULT,
  isFathomLayer,
  getFathom1000YearsDepth
} from "helpers/groupResultData";

interface Props {
  layerId: string;
  extremeMap: any;
  distanceUnit: string;
}

const ExtremeTable = ({ layerId, extremeMap, distanceUnit }: Props) => {
  const [, dispatch] = useContext(AppContext);
  const [t] = useTranslation();
  const extremeData = extremeMap.get(layerId);

  const handleCoordinateClick = (type: "max" | "min") => {
    if (!extremeData?.[type]) return;

    const longitude = Number(extremeData[type].longitude);
    const latitude = Number(extremeData[type].latitude);
    if (Number.isNaN(longitude) || Number.isNaN(latitude)) return;

    dispatch({
      type: AppAction.setMapClickEvent,
      payload: {
        lnglat: {
          getLng: () => longitude,
          getLat: () => latitude
        }
      }
    });
  };

  const handleGetTableTitle = () => {
    if (layerId.includes("Fathom")) {
      return t("legend.Maximum Flood Depth[m]");
    } else if (layerId === "WS_Windspeed_Global_SwissRe") {
      return t("legend.50y Peak Gust");
    } else {
      return t("legend." + EXTREME_KEY_LAYER_RESULT[layerId].key);
    }
  };

  const handleGetTableExtremeValue = (type: "max" | "min") => {
    if (!extremeData) return "";
    if (layerId.includes("Fathom")) {
      const depth = getFathom1000YearsDepth(
        extremeData[type],
        isFathomLayer(layerId) ? "Undefended" : "Defended"
      );
      const depthValue = parseFloat(depth);
      if (isNaN(depthValue)) {
        return t("legend." + depthValue);
      } else {
        return depth;
      }
    } else {
      if (EXTREME_KEY_LAYER_RESULT[layerId].type === "number") {
        return extremeData[type].details[EXTREME_KEY_LAYER_RESULT[layerId].key];
      } else {
        return t(
          "legend." +
          extremeData[type].details[EXTREME_KEY_LAYER_RESULT[layerId].key]
        );
      }
    }
  };

  const handleGetDistanceValue = (type: "max" | "min") => {
    const distanceInMeters = Number(
      extremeData?.[type]?.distanceFromStartMeters ??
      extremeData?.[
      type === "max"
        ? "maxDistanceFromStartMeters"
        : "minDistanceFromStartMeters"
      ]
    );

    if (!Number.isFinite(distanceInMeters)) {
      return "-";
    }

    if (distanceUnit === "km") {
      return (distanceInMeters / 1000).toFixed(3);
    }

    return distanceInMeters.toFixed(3);
  };

  return (
    <div style={{ marginTop: "15px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingRight: "10px"
        }}
      >
        <p className="m-1 font-bold">
          {t("layerId." + layerId)}{" "}
          {layerId.includes("Fathom") &&
            t("drawShape.1-in-1000-year scenario (Undefended)")}
        </p>
      </div>
      <TableContainer sx={{ width: "auto", height: "100%" }}>
        <Table
          stickyHeader
          aria-label="sticky table"
          size="small"
          style={{
            tableLayout: "fixed",
            width: "100%",
            minWidth: "100%"
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  fontSize: "12px",
                  color: "#000000de"
                }
              }}
            >
              <TableCell
                align="left"
                style={{
                  width: 100,
                  whiteSpace: "nowrap",
                  padding: "8px"
                }}
              >
                {t("drawShape.Extreme Values")}
              </TableCell>
              <TableCell
                align="left"
                style={{
                  width: 130,
                  whiteSpace: "nowrap",
                  padding: "8px"
                }}
              >
                {t("drawShape.Coordinates")}
                <Tooltip
                  placement="top"
                  title={t("drawShape.Extreme Values Table Tooltip")}
                >
                  <IconButton size="small">
                    <div
                      className="material-icons"
                      style={{ fontSize: "18px" }}
                    >
                      info_outline
                    </div>
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell
                align="right"
                style={{
                  width: 100,
                  whiteSpace: "nowrap",
                  padding: "8px"
                }}
              >{`${t("drawShape.distance")}(${distanceUnit === "km" ? t("drawShape.km") : t("drawShape.m")})`}</TableCell>
              <TableCell
                align="right"
                style={{
                  width: 120,
                  padding: "8px"
                }}
              >
                {handleGetTableTitle()}
              </TableCell>
            </TableRow>
          </TableHead>
          {extremeData && (
            <TableBody>
              <TableRow
                sx={{
                  "& td": {
                    fontSize: "13px",
                    color: "#000000de"
                  }
                }}
              >
                <TableCell align="left" style={{ padding: "8px" }}>
                  {t("drawShape.Maximum")}
                </TableCell>
                <TableCell
                  align="left"
                  style={{
                    padding: "8px",
                    color: "#007934",
                    cursor: "pointer"
                  }}
                  onClick={() => handleCoordinateClick("max")}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}
                  >
                    <span>
                      {extremeData.max.longitude +
                        ", " +
                        extremeData.max.latitude}
                    </span>

                    {extremeData.maxSameValueItems?.length > 1 && (
                      <Tooltip title={t("drawShape.Multiple Locations")}>
                        <IconButton>
                          <span
                            className="material-icons-outlined"
                            style={{
                              fontSize: "18px",
                              color: "rgb(255,160,47)"
                            }}
                          >
                            report_problem
                          </span>
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
                <TableCell align="right" style={{ padding: "8px" }}>
                  {handleGetDistanceValue("max")}
                </TableCell>
                <TableCell align="right" style={{ padding: "8px" }}>
                  {handleGetTableExtremeValue("max")}
                </TableCell>
              </TableRow>
              <TableRow
                sx={{
                  "& td": {
                    fontSize: "13px",
                    color: "#000000de"
                  }
                }}
              >
                <TableCell align="left" style={{ padding: "8px" }}>
                  {t("drawShape.Minimum")}
                </TableCell>
                <TableCell
                  align="left"
                  style={{
                    padding: "8px",
                    color: "#007934",
                    cursor: "pointer"
                  }}
                  onClick={() => handleCoordinateClick("min")}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}
                  >
                    <span>
                      {extremeData.min.longitude +
                        ", " +
                        extremeData.min.latitude}
                    </span>
                    {extremeData.minSameValueItems?.length > 1 && (
                      <Tooltip title={t("drawShape.Multiple Locations")}>
                        <IconButton>
                          <span
                            className="material-icons-outlined"
                            style={{
                              fontSize: "18px",
                              color: "rgb(255,160,47)"
                            }}
                          >
                            report_problem
                          </span>
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
                <TableCell align="right" style={{ padding: "8px" }}>
                  {handleGetDistanceValue("min")}
                </TableCell>
                <TableCell align="right" style={{ padding: "8px" }}>
                  {handleGetTableExtremeValue("min")}
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </div>
  );
};

export default ExtremeTable;
